import React, { useState } from 'react';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

const TDS_SECTIONS = {
  salary: { name: 'Salary (Section 192)', rate: 'As per slab', threshold: 250000 },
  fd_interest: { name: 'FD Interest (Section 194A)', rate: 10, threshold: 40000, seniorThreshold: 50000 },
  rent_land: { name: 'Rent - Land/Building (Section 194I)', rate: 10, threshold: 240000 },
  rent_plant: { name: 'Rent - Plant/Machinery (Section 194I)', rate: 2, threshold: 240000 },
  professional: { name: 'Professional Fees (Section 194J)', rate: 10, threshold: 30000 },
  technical: { name: 'Technical Services (Section 194J)', rate: 2, threshold: 30000 },
  contractor: { name: 'Contractor (Section 194C)', rate: 1, rateCompany: 2, threshold: 30000, aggregateThreshold: 100000 },
  commission: { name: 'Commission (Section 194H)', rate: 5, threshold: 15000 },
  lottery: { name: 'Lottery/Game (Section 194B)', rate: 30, threshold: 10000 },
  property_sale: { name: 'Property Sale (Section 194IA)', rate: 1, threshold: 5000000 },
  rent_194ib: { name: 'Rent by Individual (Section 194IB)', rate: 5, threshold: 600000 },
  epf_withdrawal: { name: 'EPF Withdrawal (Section 192A)', rate: 10, threshold: 50000 },
  dividend: { name: 'Dividend (Section 194)', rate: 10, threshold: 5000 },
};

const TDSCalculator = () => {
  const [tdsType, setTdsType] = useState('fd_interest');
  const [amount, setAmount] = useState(100000);
  const [panAvailable, setPanAvailable] = useState(true);
  const [isSeniorCitizen, setIsSeniorCitizen] = useState(false);
  const [isCompany, setIsCompany] = useState(false);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const amt = Number(amount) || 0;
    const section = TDS_SECTIONS[tdsType];
    let threshold = section.threshold;
    
    // Senior citizen threshold for FD
    if (tdsType === 'fd_interest' && isSeniorCitizen) {
      threshold = section.seniorThreshold;
    }

    let tdsRate = section.rate;
    if (tdsType === 'contractor' && isCompany) {
      tdsRate = section.rateCompany;
    }

    // If no PAN, TDS is 20% or actual rate, whichever is higher
    if (!panAvailable && typeof tdsRate === 'number') {
      tdsRate = Math.max(tdsRate, 20);
    }

    let tdsAmount = 0;
    let isTdsApplicable = false;
    let reason = '';

    if (typeof tdsRate === 'number') {
      if (amt >= threshold) {
        isTdsApplicable = true;
        tdsAmount = (amt * tdsRate) / 100;
        reason = `Amount exceeds threshold of ${formatCurrency(threshold)}`;
      } else {
        reason = `Amount below threshold of ${formatCurrency(threshold)}. No TDS applicable.`;
      }
    } else {
      isTdsApplicable = true;
      reason = 'TDS calculated as per income tax slab rates';
    }

    const netAmount = amt - tdsAmount;
    const effectiveRate = amt > 0 ? ((tdsAmount / amt) * 100).toFixed(2) : 0;

    setResult({
      sectionName: section.name,
      grossAmount: amount,
      threshold,
      tdsRate: typeof tdsRate === 'number' ? `${tdsRate}%` : tdsRate,
      tdsAmount,
      netAmount,
      isTdsApplicable,
      reason,
      effectiveRate,
      panAvailable,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('TDS Calculation Report', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Description', 'Value']],
      body: [
        ['TDS Section', result.sectionName],
        ['Gross Amount', formatCurrencyForPDF(result.grossAmount)],
        ['Threshold Limit', formatCurrencyForPDF(result.threshold)],
        ['TDS Rate', result.tdsRate],
        ['PAN Available', result.panAvailable ? 'Yes' : 'No (Higher Rate Applied)'],
        ['TDS Deductible', result.isTdsApplicable ? 'Yes' : 'No'],
        ['TDS Amount', formatCurrencyForPDF(result.tdsAmount)],
        ['Net Amount Payable', formatCurrencyForPDF(result.netAmount)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('TDS_Calculation.pdf');
  };

  return (
    <div className="calculator-page">
      <SEO 
        title="TDS Calculator"
        description="Free TDS Calculator India - Calculate Tax Deducted at Source for salary, interest, rent, professional fees."
        keywords="TDS calculator, TDS calculator India, TDS on salary, TDS on rent, TDS on interest, TDS rates calculator"
        canonicalPath="/tds"
      />
      <div className="calculator-header">
        <h1>📋 TDS Calculator</h1>
        <p>Calculate Tax Deducted at Source for various income types</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>TDS Category</label>
            <select value={tdsType} onChange={(e) => setTdsType(e.target.value)}>
              {Object.entries(TDS_SECTIONS).map(([key, value]) => (
                <option key={key} value={key}>{value.name}</option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label>Amount (₹)</label>
            <input
              type="number"
              value={amount === '' ? '' : amount}
              onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={panAvailable}
                onChange={(e) => setPanAvailable(e.target.checked)}
                style={{ width: 'auto' }}
              />
              PAN Available (Higher rate if not available)
            </label>
          </div>

          {tdsType === 'fd_interest' && (
            <div className="input-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={isSeniorCitizen}
                  onChange={(e) => setIsSeniorCitizen(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                Senior Citizen (60+ years) - Higher threshold applies
              </label>
            </div>
          )}

          {tdsType === 'contractor' && (
            <div className="input-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={isCompany}
                  onChange={(e) => setIsCompany(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                Payee is a Company (2% rate instead of 1%)
              </label>
            </div>
          )}

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate TDS
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ 
                background: result.isTdsApplicable 
                  ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                  : 'linear-gradient(135deg, #10b981, #059669)'
              }}>
                <h3>TDS Amount</h3>
                <div className="amount">{formatCurrency(result.tdsAmount)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  {result.isTdsApplicable ? `Rate: ${result.tdsRate}` : 'No TDS Applicable'}
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Section</span>
                  <span className="value" style={{ fontSize: '13px' }}>{result.sectionName}</span>
                </div>
                <div className="result-item">
                  <span className="label">Gross Amount</span>
                  <span className="value">{formatCurrency(result.grossAmount)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Threshold Limit</span>
                  <span className="value">{formatCurrency(result.threshold)}</span>
                </div>
                <div className="result-item">
                  <span className="label">TDS Rate</span>
                  <span className="value">{result.tdsRate}</span>
                </div>
                {!result.panAvailable && (
                  <div className="result-item" style={{ background: '#fef3c7' }}>
                    <span className="label">⚠️ No PAN</span>
                    <span className="value" style={{ color: '#f59e0b' }}>Higher Rate Applied (20%)</span>
                  </div>
                )}
                <div className="result-item" style={{ background: '#fef2f2' }}>
                  <span className="label">TDS Deducted</span>
                  <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(result.tdsAmount)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Net Amount Receivable</span>
                  <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.netAmount)}</span>
                </div>
              </div>

              <div className="info-box" style={{ marginTop: '16px' }}>
                <p><strong>Note:</strong> {result.reason}</p>
              </div>

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download PDF Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Select TDS category and enter amount to calculate</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Important TDS Rules</h4>
        <ul>
          <li>TDS must be deposited by 7th of next month</li>
          <li>Form 26AS shows all TDS deducted against your PAN</li>
          <li>If PAN not provided, TDS is deducted at 20% or applicable rate, whichever is higher</li>
          <li>Form 15G/15H can be submitted to avoid TDS if total income is below taxable limit</li>
        </ul>
      </div>
    </div>
  );
};

export default TDSCalculator;
